/**
 * Исходник Worker-а для виджета «SQL».
 * Внутри — SQLite (sql.js, WASM с CDN). База пересоздаётся перед каждым запуском,
 * поэтому INSERT/UPDATE ученика не портят данные для следующей задачи.
 */
export const SQL_WORKER_SOURCE = String.raw`
var SQLJS_BASE = "https://cdn.jsdelivr.net/npm/sql.js@1.11.0/dist/";
var sqlPromise = null;

function ensureSql() {
  if (sqlPromise) return sqlPromise;
  sqlPromise = new Promise(function (resolve, reject) {
    try {
      importScripts(SQLJS_BASE + "sql-wasm.js");
    } catch (e) {
      reject(new Error("Не удалось загрузить SQLite (нет интернета?): " + (e && e.message ? e.message : e)));
      return;
    }
    if (typeof initSqlJs !== "function") {
      reject(new Error("Библиотека sql.js не загрузилась"));
      return;
    }
    initSqlJs({ locateFile: function (f) { return SQLJS_BASE + f; } }).then(resolve, reject);
  });
  return sqlPromise;
}

function parseDateParts(v) {
  var s = String(v == null ? "" : v);
  var d = { y: 0, mo: 0, d: 0, h: 0, mi: 0, s: 0 };
  var dm = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (dm) { d.y = +dm[1]; d.mo = +dm[2]; d.d = +dm[3]; }
  var tm = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (tm) { d.h = +tm[1]; d.mi = +tm[2]; d.s = tm[3] ? +tm[3] : 0; }
  return d;
}

function toEpochMs(v) {
  var p = parseDateParts(v);
  if (!p.y) return Date.UTC(1970, 0, 1, p.h, p.mi, p.s);
  return Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
}

/** Функции MySQL, которых нет в SQLite — чтобы запросы из учебника не падали. */
function registerCompat(db) {
  function reg(name, fn) {
    try { db.create_function(name, fn); } catch (e) { /* не критично */ }
  }
  reg("YEAR", function (v) { return parseDateParts(v).y || null; });
  reg("MONTH", function (v) { return parseDateParts(v).mo || null; });
  reg("DAY", function (v) { return parseDateParts(v).d || null; });
  reg("DAYOFMONTH", function (v) { return parseDateParts(v).d || null; });
  reg("HOUR", function (v) { return parseDateParts(v).h; });
  reg("MINUTE", function (v) { return parseDateParts(v).mi; });
  reg("SECOND", function (v) { return parseDateParts(v).s; });
  reg("NOW", function () { return new Date().toISOString().slice(0, 19).replace("T", " "); });
  reg("CURDATE", function () { return new Date().toISOString().slice(0, 10); });
  reg("DATEDIFF", function (a, b) {
    return Math.round((toEpochMs(a) - toEpochMs(b)) / 86400000);
  });
  reg("TIMESTAMPDIFF", function (unit, a, b) {
    var ms = toEpochMs(b) - toEpochMs(a);
    var u = String(unit || "").toUpperCase();
    if (u === "SECOND") return Math.trunc(ms / 1000);
    if (u === "MINUTE") return Math.trunc(ms / 60000);
    if (u === "HOUR") return Math.trunc(ms / 3600000);
    if (u === "DAY") return Math.trunc(ms / 86400000);
    if (u === "WEEK") return Math.trunc(ms / (7 * 86400000));
    if (u === "MONTH" || u === "YEAR") {
      var pa = parseDateParts(a), pb = parseDateParts(b);
      var months = (pb.y - pa.y) * 12 + (pb.mo - pa.mo);
      if (pb.d < pa.d) months -= 1;
      return u === "MONTH" ? months : Math.trunc(months / 12);
    }
    return null;
  });
  reg("IF", function (cond, a, b) { return cond ? a : b; });
  reg("LEFT", function (s, n) { return String(s == null ? "" : s).slice(0, Number(n) || 0); });
  reg("RIGHT", function (s, n) {
    var str = String(s == null ? "" : s);
    var k = Number(n) || 0;
    return k <= 0 ? "" : str.slice(-k);
  });
  reg("RAND", function () { return Math.random(); });
  function pad2(n) { return (n < 10 ? "0" : "") + n; }
  reg("TIMEDIFF", function (a, b) {
    var ms = toEpochMs(a) - toEpochMs(b);
    var neg = ms < 0;
    var s = Math.round(Math.abs(ms) / 1000);
    var out = pad2(Math.floor(s / 3600)) + ":" + pad2(Math.floor((s % 3600) / 60)) + ":" + pad2(s % 60);
    return neg ? "-" + out : out;
  });
  reg("TIME_TO_SEC", function (v) {
    var p = parseDateParts(v);
    return p.h * 3600 + p.mi * 60 + p.s;
  });
  reg("SEC_TO_TIME", function (v) {
    var s = Math.round(Number(v) || 0);
    var neg = s < 0;
    s = Math.abs(s);
    var out = pad2(Math.floor(s / 3600)) + ":" + pad2(Math.floor((s % 3600) / 60)) + ":" + pad2(s % 60);
    return neg ? "-" + out : out;
  });
  var MONTHS = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];
  reg("MONTHNAME", function (v) {
    var m = parseDateParts(v).mo;
    return m ? MONTHS[m - 1] : null;
  });
  reg("DATE_FORMAT", function (v, fmt) {
    var p = parseDateParts(v);
    var map = {
      Y: String(p.y), y: pad2(p.y % 100),
      m: pad2(p.mo), c: String(p.mo), M: p.mo ? MONTHS[p.mo - 1] : "",
      d: pad2(p.d), e: String(p.d),
      H: pad2(p.h), k: String(p.h), i: pad2(p.mi), s: pad2(p.s)
    };
    return String(fmt == null ? "" : fmt).replace(/%(.)/g, function (all, ch) {
      return Object.prototype.hasOwnProperty.call(map, ch) ? map[ch] : ch;
    });
  });
  reg("LOCATE", function (needle, hay) {
    return String(hay == null ? "" : hay).indexOf(String(needle == null ? "" : needle)) + 1;
  });
  reg("SUBSTRING_INDEX", function (str, delim, count) {
    var parts = String(str == null ? "" : str).split(String(delim));
    var n = Number(count) || 0;
    return n >= 0 ? parts.slice(0, n).join(delim) : parts.slice(n).join(delim);
  });
}

function cell(v) {
  if (v == null) return null;
  if (v instanceof Uint8Array) return "<двоичные данные>";
  return v;
}

var MAX_ROWS = 500;

/** Выполняет скрипт и возвращает результат последнего SELECT. */
function runScript(db, sql) {
  var stmts = [];
  try {
    stmts = db.exec(sql);
  } catch (e) {
    return { error: String(e && e.message ? e.message : e) };
  }
  var changed = db.getRowsModified();
  if (!stmts.length) {
    return {
      columns: [],
      rows: [],
      note: changed > 0 ? "Запрос выполнен, изменено строк: " + changed : "Запрос выполнен, таблица результата пустая.",
    };
  }
  var last = stmts[stmts.length - 1];
  var rows = [];
  var total = last.values.length;
  for (var i = 0; i < total && i < MAX_ROWS; i++) {
    var src = last.values[i];
    var row = [];
    for (var j = 0; j < src.length; j++) row.push(cell(src[j]));
    rows.push(row);
  }
  return {
    columns: last.columns.slice(),
    rows: rows,
    total: total,
    truncated: total > MAX_ROWS,
  };
}

function buildDb(SQL, setup) {
  var db = new SQL.Database();
  registerCompat(db);
  if (setup && setup.trim()) {
    try {
      db.run(setup);
    } catch (e) {
      db.close();
      throw new Error("Ошибка в скрипте базы: " + (e && e.message ? e.message : e));
    }
  }
  return db;
}

self.onmessage = function (ev) {
  var msg = ev.data || {};
  if (msg.type !== "run") return;
  var reqId = msg.reqId;
  self.postMessage({ type: "phase", reqId: reqId, phase: "loading_runtime" });

  ensureSql().then(function (SQL) {
    self.postMessage({ type: "phase", reqId: reqId, phase: "running" });
    var db = null;
    var out = { type: "result", reqId: reqId };
    try {
      db = buildDb(SQL, msg.setup);
      out.actual = runScript(db, String(msg.query || ""));
      out.schema = readSchema(db);
    } catch (e) {
      out.actual = { error: String(e && e.message ? e.message : e) };
    } finally {
      if (db) { try { db.close(); } catch (e2) { /* ignore */ } }
    }

    if (msg.solution) {
      var db2 = null;
      try {
        db2 = buildDb(SQL, msg.setup);
        out.expected = runScript(db2, String(msg.solution));
      } catch (e3) {
        out.expected = { error: String(e3 && e3.message ? e3.message : e3) };
      } finally {
        if (db2) { try { db2.close(); } catch (e4) { /* ignore */ } }
      }
    }
    self.postMessage(out);
  }, function (err) {
    self.postMessage({
      type: "result",
      reqId: reqId,
      actual: { error: String(err && err.message ? err.message : err) },
    });
  });
};

/** Список таблиц и столбцов — для панели «Схема» и для своей базы. */
function readSchema(db) {
  var tables = [];
  try {
    var res = db.exec("SELECT name FROM sqlite_master WHERE type='table' AND name NOT LIKE 'sqlite_%' ORDER BY name");
    if (!res.length) return tables;
    for (var i = 0; i < res[0].values.length; i++) {
      var name = String(res[0].values[i][0]);
      var cols = [];
      var info = db.exec("PRAGMA table_info('" + name.replace(/'/g, "''") + "')");
      if (info.length) {
        for (var j = 0; j < info[0].values.length; j++) {
          cols.push({
            name: String(info[0].values[j][1]),
            type: String(info[0].values[j][2] || ""),
            pk: Boolean(info[0].values[j][5]),
          });
        }
      }
      var count = 0;
      try {
        var c = db.exec('SELECT COUNT(*) FROM "' + name.replace(/"/g, '""') + '"');
        if (c.length) count = Number(c[0].values[0][0]) || 0;
      } catch (e) { /* ignore */ }

      // Связи (REFERENCES) — по ним рисуется диаграмма.
      var fks = [];
      try {
        var fkRes = db.exec("PRAGMA foreign_key_list('" + name.replace(/'/g, "''") + "')");
        if (fkRes.length) {
          for (var k = 0; k < fkRes[0].values.length; k++) {
            var row = fkRes[0].values[k];
            fks.push({
              from: String(row[3]),
              table: String(row[2]),
              to: row[4] == null ? null : String(row[4]),
            });
          }
        }
      } catch (e) { /* ignore */ }

      tables.push({ name: name, columns: cols, rows: count, fks: fks });
    }
  } catch (e) { /* ignore */ }
  return tables;
}
`;
